import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronsRight,
  ChevronUp,
  Clock,
  Loader2,
  CloudOff,
  // Installed lucide-react version exports the pre-rename name.
  UploadCloud as CloudUpload,
  Maximize2,
  Mic,
  MicOff,
  Minimize2,
  Pause,
  Play,
  Shield,
  Square,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { LedgerPickupRail } from '@/components/medicalRecords/encounter/LedgerPickupRail';
import { medicalRecordsKeys } from '@/hooks/useMedicalRecords';
import { useRecordingSessionStore, getLiveElapsedSeconds } from '@/stores/useRecordingSessionStore';
import { recordingSessionApi } from '@/services/recordingSessionApi';
import { drainIdbToRest } from '@/services/audioChunkDrain';
import { useLiveRecording, type TranscriptSegment } from '@/hooks/useLiveRecording';
import { useRecordingLimits } from '@/hooks/useSystemSettings';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { SynthOverlay } from '@/components/encounter/SynthOverlay';
import { useSynthJobsStore } from '@/stores/useSynthJobsStore';
import { useDockLayoutStore } from '@/stores/useDockLayoutStore';
import {
  setCompletionIntent,
  hasCompletionIntent,
  clearCompletionIntent,
} from '@/lib/completionIntent';
import {
  isTabRecorder,
  clearTabRecorderMark,
} from '@/lib/recordingTabMarker';
import { readDemoScript, clearDemoScript } from '@/services/demoConsultApi';
import { MaieConsultStage, type CollectedRow } from '@/components/consult/MaieConsultStage';
import { isMaieShell } from '@/config/maieShell';
import { splitAllergyText, allergenOf } from '@/utils/allergyText';
import { patientKeys } from '@/hooks/usePatients';
import { patientApi } from '@/services/patientApi';
import { noteRoute } from '@/config/noteRoute';
import { homeRoute } from '@/config/homeRoute';
import { useShellIdentity } from '@/components/shell/useShellIdentity';

/**
 * Build the live-pane seed from the session's persisted transcript.
 *
 * Preferred source: `transcriptSegments` — the structured diarized turns
 * the scribe processor rewrites alongside the flat transcript — so a
 * refresh / cross-device handover keeps its speaker chips. Rows from
 * before that column existed (or sessions whose segments write failed)
 * fall back to the old behaviour: the flat transcript as one anonymous
 * paragraph, with only post-seed partials carrying speakers.
 */
const seedSegmentsFromTranscript = (
  text: string | null | undefined,
  segments?:
    | Array<{ text: string; speaker?: string; chunkIndex?: number }>
    | null,
): TranscriptSegment[] => {
  if (segments && segments.length > 0) {
    const now = Date.now();
    return segments
      .filter((s) => s.text?.trim())
      .map((s, i) => ({
        id: `seed-${s.chunkIndex ?? 0}-${i}`,
        text: s.text.trim(),
        speaker: s.speaker,
        chunkIndex: s.chunkIndex,
        receivedAt: now,
      }));
  }
  const t = text?.trim();
  if (!t) return [];
  return [{ id: 'seed-prior-transcript', text: t, receivedAt: Date.now() }];
};

/**
 * Persistent recording surface. Mounted at app-shell level so it survives
 * route changes. Streams mic audio through the AI Gateway scribe pipeline,
 * shows a live waveform driven by real audio amplitude, and renders the
 * incoming transcript with speaker labels (when diarization is on).
 */

const formatSeconds = (s: number) => {
  const safe = Number.isFinite(s) && s >= 0 ? Math.floor(s) : 0;
  const m = Math.floor(safe / 60);
  const sec = safe % 60;
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
};

export const RecordingDock: React.FC = () => {
  const { isRTL } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    session,
    tick,
    pause,
    resume,
    abandon,
    rehydrate,
  } = useRecordingSessionStore();
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Snapshots the elapsed time the moment finalize/synth starts. Lives at
  // the top of the component so it stays above the `if (!session) return
  // null` early return — Hooks must run in the same order every render.
  const frozenElapsedRef = useRef<number | null>(null);
  const requestedSessionId = useMemo(
    () => new URLSearchParams(location.search).get('recordingSessionId'),
    [location.search],
  );
  const passiveAttachMode = Boolean(requestedSessionId);

  // Demo consult: the read-aloud script the doctor saw on /welcome
  // vanished the moment recording started (we navigate to /home).
  // WelcomePage stashes it in sessionStorage keyed by the demo patient;
  // surface it here so they can keep reading mid-recording. Lives above
  // the `if (!session) return null` early return — hook order.
  const [demoScriptDismissed, setDemoScriptDismissed] = useState(false);
  const demoScript = useMemo(() => {
    if (demoScriptDismissed) return null;
    const stashed = readDemoScript();
    if (!stashed || stashed.patientId !== session?.patientId) return null;
    return (isRTL ? stashed.ar : stashed.en) || null;
  }, [demoScriptDismissed, session?.patientId, isRTL]);
  const dismissDemoScript = () => {
    setDemoScriptDismissed(true);
    clearDemoScript();
  };

  useEffect(() => {
    if (requestedSessionId) return;
    void rehydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSessionId]);

  useEffect(() => {
    if (!session) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session, tick]);

  // Live audio capture + transcription via the AI Gateway scribe pipeline.
  // No HTTP polling — chunks stream over the socket, the gateway emits
  // partial transcript events, and `endRecording()` fires the final
  // `scribe.end-recording` over the same socket and resolves with the
  // generated SOAP / medical-record id.
  // Seed the live transcript from any persisted transcript on the session
  // — refreshed/handed-over clients pick up where the consult left off
  // instead of starting blank. Recomputed when `session?.transcript`
  // changes so the post-`rehydrate()` server snapshot replaces the
  // (often stale or empty) localStorage-hydrated value. The pre-fix
  // useRef(seedSegmentsFromTranscript(session?.transcript)) froze the
  // seed at first render — at which point Zustand had populated session
  // from localStorage but the fresh server transcript hadn't landed
  // yet — leaving the live pane blank after every reload even though
  // the SynthOverlay's transcript field rendered fine.
  // useLiveRecording itself is responsible for applying the seed only
  // when its own segments array is still empty, so this can change as
  // many times as transcript-rehydrate fires without clobbering live
  // segments that arrived first.
  const initialSegments = useMemo(
    () =>
      seedSegmentsFromTranscript(
        session?.transcript,
        session?.transcriptSegments,
      ),
    [session?.transcript, session?.transcriptSegments],
  );

  // Recovery driver retry gate. Lifted to component body so the
  // cold-mount recovery handlers (declared below) can reset it on
  // explicit "Stop + retry" / "Resume" actions. The driver itself
  // owns the actual retry logic further down.
  const recoveryFiredRef = useRef(false);

  // Cold-mount recovery gate. The sessionStorage tab marker is set by
  // useLiveRecording.start() (and eagerly by the store's startSession)
  // and survives a same-tab refresh but dies with the tab and never
  // leaks across tabs. So:
  //   - Same-tab refresh while recording → marker present → auto-resume.
  //   - Fresh tab / different device / reopened-after-crash → marker
  //     absent → block mic acquisition, show recovery banner.
  //
  // Fires for BOTH `capturing` and `paused` rows when the marker is
  // absent. The `paused` branch covers the post-MainLayout-rehydrate
  // path: the cold-boot reconcile auto-pauses a `capturing` row that
  // belongs to a different tab/device, then this gate surfaces the
  // recovery options. The `capturing` branch covers the brief window
  // before that auto-pause lands (or the case where it failed —
  // network blip, race, etc.). Independent of the server-side stale-
  // capturing cron, which is a longer-horizon backstop.
  //
  // Implementation note: kept as `useState(false)` + a post-render
  // useEffect on purpose. A previous attempt computed the gate
  // synchronously, which fired the recovery banner on EVERY cold
  // app-open with a persisted session (the marker only lands after
  // the hook's first start(), and sync evaluation didn't give that
  // first render a chance to run). The current pattern lets the first
  // render proceed with the gate closed; the effect re-evaluates
  // after layout and surfaces the banner only if the hook didn't
  // claim the session within the fresh-session window.
  const FRESH_SESSION_GRACE_MS = 10_000;
  const [coldMountRecovering, setColdMountRecovering] = useState(false);
  useEffect(() => {
    if (!session?.id) return;
    if (session.status !== 'capturing' && session.status !== 'paused') {
      setColdMountRecovering(false);
      return;
    }
    const ts = session.lastResumedAtMs ?? session.startedAt ?? 0;
    const isFresh = ts > 0 && Date.now() - ts < FRESH_SESSION_GRACE_MS;
    if (isFresh) {
      setColdMountRecovering(false);
      return;
    }
    setColdMountRecovering(!isTabRecorder(session.id));
  }, [session?.id, session?.status, session?.lastResumedAtMs]);

  // Per-tenant recording limits (max session length + silence auto-terminate).
  // Falls back to defaults (60 min / 5 min) until the query lands.
  const recordingLimits = useRecordingLimits();
  const maxRecordingSeconds = recordingLimits.maxRecordingMinutes * 60;

  const {
    bars,
    segments,
    connected,
    ready,
    error: liveErr,
    pendingChunks,
    noAudioWarning,
    silenceCountdown,
    extendSilence,
    endRecording,
    driveRecovery,
  } = useLiveRecording({
    sessionId: session?.id ?? null,
    recordingSessionId: session?.id ?? null,
    patientId: session?.patientId ?? null,
    // Picker-driven flows (EncounterOverlay) seed `templateId` on the
    // session and the row persists it (recording_sessions.template_id),
    // so a refresh / auth-driven WS reconnect rehydrates with the right
    // value. Ad-hoc dock starts (DoctorHome, patient header) leave it
    // null; we forward `null` to the gateway, which falls through to the
    // doctor's specialty default — the synthetic SOAP_SEED_ID is a UI-
    // only sentinel that fails the backend's UUID guard, so we must not
    // send it on the wire.
    templateId: session?.templateId ?? null,
    // Per-encounter speaking language, stamped on the row from the arm panel
    // (or the doctor's pref for direct starts). Passing it here makes the LIVE
    // STT honor a one-off override instead of always using the global pref.
    language: session?.language ?? undefined,
    // Dictation → the whole pipeline skips diarization (faster, single voice).
    captureMode: session?.captureMode ?? 'visit',
    // Only live-capture when the backend session is actually in a live
    // recording state AND we're confident this tab owns the recording
    // (stale/crashed sessions wait for the doctor to explicitly Resume
    // via the recovery banner, see coldMountRecovering above).
    enabled: Boolean(
      !passiveAttachMode &&
      session &&
        !coldMountRecovering &&
        (session.status === 'capturing' || session.status === 'paused'),
    ),
    paused: Boolean(session?.paused),
    initialSegments,
    // Sustained silence auto-finalizes whatever was captured rather than
    // recording dead air. handleComplete is defined below; the closure
    // resolves it at call time (the hook only invokes this from its rAF loop,
    // never during render). Budget is per-tenant (0 = disabled).
    onInactivityTimeout: () => void handleComplete(),
    silenceTimeoutMs: recordingLimits.silenceTimeoutMinutes * 60_000,
  });

  const handleRecoveryResume = async () => {
    if (!session?.id) return;
    // The doctor explicitly chose to resume on this device. Flip the
    // server row to `paused → capturing` (no-op if already capturing,
    // which is the common case here — we just need a fresh
    // lastResumedAt). Then drop the cold-mount gate so useLiveRecording
    // engages and the mic acquires.
    try {
      if (session.status === 'paused') {
        await resume();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Resume failed');
      return;
    }
    setColdMountRecovering(false);
  };

  const handleRecoveryStopAndRetry = async () => {
    if (!session?.id) return;
    // Treat the recording as ended-on-the-other-device. Pause server-
    // side, then route the doctor through the same Pending Recordings
    // retry path so any audio that did make it to MinIO can be
    // synthesized into a SOAP note from here. We intentionally don't
    // call the stop API directly — pause + retry preserves the
    // ability to resume later if the doctor changes their mind.
    try {
      await recordingSessionApi.pause(session.id).catch(() => {});
      setColdMountRecovering(false);
      // Defer to the existing retry-processing UX (toast lifecycle +
      // AUDIO_NOT_ON_SERVER fallback) by tripping completion intent
      // and asking the recovery driver to fire on the next render.
      setCompletionIntent(session.id);
      recoveryFiredRef.current = false;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    }
  };

  /**
   * Discard confirm state. `'live'` = the in-flow discard (doctor is
   * actively capturing/paused and about to throw the consult away, message
   * includes the elapsed time); `'recovery'` = the recovery-banner discard.
   * Both funnel into `performDiscard` — same destructive consequence as
   * `abandon()`: server marks the row abandoned, IDB chunks discarded,
   * session cleared from the store. Rendered via the themed
   * ConfirmationModal (focus-trapped, Esc/click-outside, danger variant)
   * instead of the old unstyled `window.confirm`.
   */
  const [discardConfirm, setDiscardConfirm] = useState<
    'live' | 'recovery' | null
  >(null);
  const [discarding, setDiscarding] = useState(false);

  const handleConfirmDiscard = () => {
    if (!session?.id) return;
    setDiscardConfirm('live');
  };

  const handleRecoveryDiscard = () => {
    if (!session?.id) return;
    setDiscardConfirm('recovery');
  };

  const performDiscard = async () => {
    if (!session?.id) return;
    setDiscarding(true);
    try {
      clearTabRecorderMark(session.id);
      clearCompletionIntent(session.id);
      await abandon();
      // abandon() also discards any draft the session had committed
      // (server-side) — refresh the "needs review" list so that draft
      // disappears immediately instead of lingering until the next reload.
      void queryClient.invalidateQueries({
        queryKey: medicalRecordsKeys.lists(),
      });
      // abandon() clears the session → the dock (and this modal) unmount;
      // the reset below is belt-and-braces for the error-free-but-still-
      // mounted edge (passive attach).
      setDiscardConfirm(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Discard failed');
    } finally {
      setDiscarding(false);
    }
  };

  useEffect(() => {
    if (!session) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
      if (e.code === 'Space') {
        e.preventDefault();
        void (session.paused ? resume() : pause());
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        void handleComplete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, session?.paused]);

  // Refresh-while-recording guard. The MediaRecorder dies the moment
  // the page unloads, regardless of what we do — but flushing pending
  // IDB chunks and giving the doctor a chance to cancel the navigation
  // is the difference between "lost a few seconds at the boundary" and
  // "lost the rotation that was mid-flight when refresh hit". The next
  // mount picks up from IDB so we don't need to coordinate server-side
  // state on unload (the row stays in `capturing`, the next mount
  // replays IDB and resumes mic capture from the next chunk index).
  useEffect(() => {
    if (!session) return;
    if (session.status !== 'capturing' || session.paused) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chromium ignores the custom string; setting returnValue is what
      // actually triggers the prompt across browsers. The displayed
      // message is the browser default ("Leave site?") in modern UAs.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [session?.id, session?.status, session?.paused]);

  // HTTP commit fallback. Used only if the gateway's auto-commit didn't
  // return a medicalRecordId (e.g. legacy gateway, or a session that hit
  // the no-patient guard server-side after we've already passed our own).
  // The new-flow path is gateway-driven and shouldn't hit this.
  const ensureCommitted = useRecordingSessionStore((s) => s.ensureCommitted);
  const queryClient = useQueryClient();
  /* Where an offline finish walks the doctor to when there is no patient on
     the session — the role's own first door, not the legacy dashboard. */
  const { surface } = useShellIdentity();
  const registerSynthJob = useSynthJobsStore((s) => s.register);
  const failSynthJob = useSynthJobsStore((s) => s.fail);
  const dismissSynthJob = useSynthJobsStore((s) => s.dismiss);
  // Read the in-flight job's transcript snapshot so the synth overlay
  // can show "what the AI is reading from" — and swap to the refined
  // version the moment the post-process emits scribe.transcript-refined.
  const synthJob = useSynthJobsStore((s) =>
    session?.id ? s.jobs[session.id] : null,
  );
  // Right-rail collapse preference (shared with NextAppointmentDock).
  // The doctor toggles it from the in-rail double-chevron — preference
  // persists via the store's localStorage middleware.
  const rightRailCollapsed = useDockLayoutStore((s) => s.rightRailCollapsed);
  const setRightRailCollapsed = useDockLayoutStore(
    (s) => s.setRightRailCollapsed,
  );
  // Stage vs dock. A consult starts on the full-screen stage — the doctor
  // pressed Start on a full-screen arm panel, and handing them a 460 px
  // rail (or the 40 px sliver a persisted collapse leaves behind) in the
  // same frame is how a live recording goes missing. Minimize moves it to
  // the rail; the rail's expand control brings it back.
  const consultView = useDockLayoutStore((s) => s.consultView);
  const setConsultView = useDockLayoutStore((s) => s.setConsultView);
  // Report the mobile mini-bar's visible height to the layout store so
  // MaieLauncherPill can offset around it. The mini-bar is ~56 px tall
  // (h-10 mic icon + py-2.5 padding) — we round up so the pill clears
  // shadow + safe-area-inset on most devices.
  const setBottomHeight = useDockLayoutStore((s) => s.setBottomHeight);
  // True after the doctor explicitly minimized the synth overlay. The
  // background pill (mounted at app shell) takes over from there; the
  // dock no longer renders its overlay even though `synthInFlight` is
  // still true. Reset on session change so the next consult shows the
  // overlay by default.
  const [overlayMinimized, setOverlayMinimized] = useState(false);

  const handleComplete = async () => {
    if (!session) return;
    if (!session.patientId) {
      // No patient yet — STOP the recording so the doctor can take
      // their time creating/picking a patient without the mic still
      // capturing room audio. We deliberately do NOT set
      // `completionIntent` here: synth hasn't started, can't start
      // without a patient, and the synthOverlayVisible gate keys off
      // the intent flag — setting it now would render a "Generating
      // SOAP" surface while the doctor is still picking who the note
      // belongs to. The intent gets set inside PatientsPage's
      // handleAttachTo just before scribeEndRecording fires; the
      // optimistic `status: 'stopped'` flip is enough for the attach
      // flow's `wasStopped` check to trigger synth on its own.
      useRecordingSessionStore.setState((s) =>
        s.session && s.session.id === session.id
          ? {
              session: {
                ...s.session,
                status: 'stopped',
                paused: false,
                lastResumedAtMs: null,
              },
            }
          : {},
      );
      void recordingSessionApi.stop(session.id).catch((err) => {
        console.warn(
          '[RecordingDock] recording_session stop API failed (no-patient path)',
          err,
        );
      });
      // Same reason as handleAssign: the stage covers the picker.
      setConsultView('docked');
      navigate(`/patients?attachSession=${session.id}`);
      return;
    }
    // Persist intent BEFORE the first await — if the network drops, the
    // socket dies, or the user reloads while we're awaiting endRecording,
    // the next dock mount needs to know this session was meant to finish
    // and should retry synth instead of sitting in `paused`.
    setCompletionIntent(session.id);
    // Optimistically transition the local session row to `stopped` so
    // `synthInFlight` (which checks status === 'stopped') is true on
    // every device viewing this session, not just the tab that owns
    // the localStorage `completionIntent` flag. Pre-fix, the row sat
    // in `capturing/paused` for the entire SOAP wait + indefinitely
    // afterwards if synth timed out — leaving the dock body's
    // pause/resume buttons enabled when the doctor minimized the
    // synth overlay, which read as "Complete didn't take, I can just
    // resume." The stop API call is fired and forgotten; on success
    // it converts the optimistic flip into the canonical row state.
    // On failure (network drop) we leave the optimistic state in
    // place so the dock UI still reflects the doctor's intent.
    useRecordingSessionStore.setState((s) =>
      s.session && s.session.id === session.id
        ? {
            session: {
              ...s.session,
              status: 'stopped',
              paused: false,
              lastResumedAtMs: null,
            },
          }
        : {},
    );
    void recordingSessionApi.stop(session.id).catch((err) => {
      console.warn(
        '[RecordingDock] recording_session stop API failed',
        err,
      );
    });
    // Offline-at-Complete: don't await endRecording. Without a gateway
    // there's nothing for the await to resolve against (the per-request
    // socket callback can't fire), so we'd burn a 5-min soft timeout
    // staring at a "Generating SOAP" overlay before giving up and
    // returning the doctor to a stuck dock. Instead: persist the intent,
    // queue the session for the connection-restore drainer, navigate
    // away so the doctor can move on. The drainer + auto-retry cron
    // pick it up the moment the network is back. Doctors in flaky-net
    // clinics can record N consults this way; everything synthesizes
    // when they finally have a few good minutes of internet.
    if (!connected) {
      toast.message(
        isRTL
          ? 'لا يوجد اتصال — سنُكمل ملاحظتك عند عودة الإنترنت.'
          : "No internet — we'll process this when you're back online.",
        {
          description: isRTL
            ? 'الصوت محفوظ محلياً. يمكنك بدء زيارة جديدة الآن، وسنُجهز الملاحظات تلقائياً.'
            : 'Audio is saved on this device. Start the next visit — we batch-process when the network returns.',
          duration: 8000,
        },
      );
      // Register a "waiting" synth job so the background pill / pending
      // recordings list shows the doctor that the consult is queued.
      registerSynthJob({
        id: session.id,
        sessionId: session.id,
        patientName,
        patientId: session.patientId,
        kind: 'generate',
      });
      // Walk the doctor away — back to the patient chart (or home) so
      // they're not staring at a frozen dock. The session is in the
      // pending queue; everything resumes via the drainer / auto-retry.
      useRecordingSessionStore.setState({ session: null });
      navigate(
        session.patientId
          ? `/patients/${session.patientId}`
          : homeRoute(surface),
      );
      return;
    }
    setFinalizing(true);
    // Register the synth job in the global store FIRST so the bridge +
    // pill can pick up scribe.* events even if the doctor minimizes the
    // overlay or navigates away mid-synth. Patient name is captured here
    // because the local `session` is cleared on success and the pill
    // needs a stable label to show.
    registerSynthJob({
      id: session.id,
      sessionId: session.id,
      patientName,
      patientId: session.patientId,
      kind: 'generate',
    });
    try {
      // Drive the final SOAP through the same socket the audio chunks
      // went through. We don't navigate from here — the bridge maps the
      // resulting `scribe.soap-result` event onto the synth job in
      // `useSynthJobsStore`, and the unified navigate effect above
      // routes the doctor to /encounter/:recordId/review when the job
      // flips to 'done'. Same path same-tab + cross-tab + recovery.
      const result = await endRecording();
      const synthLanded = Boolean(
        result.medicalRecordId || result.soapNote || result.transcription,
      );
      if (!synthLanded) {
        // The await resolved without a real result — the per-request
        // callback never fired (socket died) and we hit the soft timeout.
        // Leave the session in place; the recovery effect below will pick
        // it up on the next reconnect / mount. Intent flag stays set.
        return;
      }
      // Per-request callback returned a recordId before the socket event
      // could fire? Funnel into the same navigate path so behavior is
      // identical to the cross-tab case. The bridge's socket-driven
      // `complete()` is idempotent on a job that's already 'done'.
      const recordId =
        result.medicalRecordId ?? (await ensureCommitted())?.id ?? null;
      if (recordId && session.id) {
        useSynthJobsStore.getState().complete(session.id, {
          recordId,
          patientId: session.patientId ?? null,
        });
      }
    } catch (err) {
      // Surface to the inline error chip; user can retry or discard.
      // Intent flag intentionally stays set so the recovery effect can
      // try again on the next reconnect.
      console.error('[RecordingDock] end-recording failed', err);
    } finally {
      setFinalizing(false);
    }
  };

  // Hard recording cap (per-tenant maxRecordingMinutes, default 60). A safety
  // stop so a forgotten/runaway session can't grow unbounded (and blow the
  // synthesis budgets downstream). We poll the store-derived elapsed — which
  // already excludes paused time and survives reloads — rather than a closure
  // value, and route through the normal Complete path so whatever was captured
  // still gets a SOAP note. Re-armed per session via the id key.
  const maxCapFiredRef = useRef<string | null>(null);
  useEffect(() => {
    const sid = session?.id;
    if (!sid) return;
    if (session?.status === 'stopped') return;
    if (maxCapFiredRef.current === sid) return;
    const id = setInterval(() => {
      if (maxCapFiredRef.current === sid) return;
      const live = useRecordingSessionStore.getState().session;
      if (!live || live.id !== sid) return;
      if (getLiveElapsedSeconds(live) >= maxRecordingSeconds) {
        maxCapFiredRef.current = sid;
        toast.info(
          isRTL
            ? 'تم بلوغ الحد الأقصى لمدة التسجيل — جارٍ إنهاء الزيارة.'
            : 'Maximum recording length reached — finishing up.',
        );
        void handleComplete();
      }
    }, 5_000);
    return () => clearInterval(id);
    // session.id / status + the cap are the stable keys; the live row (and
    // elapsed) is read fresh inside the interval so we don't re-arm every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.status, isRTL, maxRecordingSeconds]);

  // Recovery path: if a refresh/handover dropped the socket while SOAP
  // synthesis was still running on the backend, the scribe processor
  // persists soapDraft to the recording_sessions row when it finishes,
  // and the gateway re-emits scribe.soap-result on subsequent client
  // connections. The dock listens on the socket FIRST and only falls
  // back to HTTP polling when the socket can't be relied on.
  const draftPresent = Boolean(
    session?.soapDraft &&
      (session.soapDraft.subjective ||
        session.soapDraft.objective ||
        session.soapDraft.assessment ||
        session.soapDraft.plan),
  );
  // Server-authoritative "synth needs driving" signal. The scribe
  // processor flips stages.synth to 'in_flight' the moment it requests
  // SOAP generation and to 'synthesized'/'committed'/'failed' on
  // termination — so a refresh / cross-device handover sees the truth.
  // We treat 'failed' as still-needs-driving on the FE side: the auto-
  // retry cron handles backend retries, but the doctor can also kick it
  // by re-mounting the dock.
  const serverSynthInFlight =
    session?.stages?.synth === 'in_flight' ||
    session?.stages?.synth === 'failed';
  // Legacy localStorage flag kept as a backup when the rehydrated row
  // has no `stages` (older sessions, or a row we couldn't refetch).
  // Same-tab Complete still sets it via setCompletionIntent so the
  // immediate transition window is covered before the server flag lands.
  const completionIntentSet = Boolean(
    session?.id && hasCompletionIntent(session.id),
  );
  const synthInFlight =
    finalizing ||
    draftPresent ||
    serverSynthInFlight ||
    completionIntentSet;

  // Overlay visibility is stricter: when the soapDraft is already on
  // the rehydrated row, synth is *done*, not in flight — the unified
  // navigate effect / polling fallback will navigate within the next
  // tick, so flashing the "Generating SOAP" surface for one render
  // just looks like a stuck modal. Keep `synthInFlight` (broader) for
  // the recovery driver + polling gate.
  const synthOverlayVisible =
    finalizing ||
    serverSynthInFlight ||
    (completionIntentSet && !draftPresent);

  // Recovery driver. Fires when the dock mounts on a session that the
  // doctor previously tried to finish (intent set) but no record was
  // ever produced. We re-bind the scribe session, replay any IDB chunks
  // the prior socket never flushed, and re-emit scribe.end-recording.
  // The global scribe.soap-result subscription (below) handles the
  // navigate + intent-clear when synth finally lands.
  //
  // Gated by `connected` so we don't burn cycles trying while the
  // gateway is still down — the connection-change handler in
  // useLiveRecording also auto-fires the same retry on reconnect, so
  // this useEffect mostly covers the cold-mount-after-refresh case.
  // (`recoveryFiredRef` is declared higher up so the cold-mount
  // recovery handlers can reset it.)
  useEffect(() => {
    if (!session) {
      recoveryFiredRef.current = false;
      return;
    }
    if (finalizing) return; // same-tab Complete is in flight
    if (draftPresent) return; // synth already landed; polling fallback navigates
    // Recovery fires when EITHER signal says synth needs driving:
    //   - Server: stages.synth in {'in_flight', 'failed'} → authoritative,
    //     survives device switches.
    //   - Client: completionIntent set → covers the case where rehydrate
    //     failed (offline) or the row predates stage tracking.
    if (!serverSynthInFlight && !completionIntentSet) return;
    if (!connected) return;
    if (recoveryFiredRef.current) return;
    recoveryFiredRef.current = true;
    // Passive recovery for refresh / attach-after-finish: if we already
    // have a persisted transcript on the session row, skip the socket-
    // pipeline replay (the partial transcript means the gateway has
    // already seen audio) and ask the backend to reprocess from durable
    // server-side state. BUT first we must drain any IDB chunks that
    // were captured locally and never uploaded — otherwise reprocess
    // runs against partial audio (last 30 s missing) and the SOAP is
    // silently truncated.
    if (session.transcript?.trim()) {
      void (async () => {
        try {
          const drain = await drainIdbToRest(session.id);
          if (drain.failed > 0 && drain.uploaded === 0 && drain.total > 0) {
            // Total drain failure — bail before reprocess so we don't
            // synth against a stale audio set. Recovery driver re-arms
            // on next reconnect.
            recoveryFiredRef.current = false;
            return;
          }
        } catch (err) {
          console.warn(
            '[RecordingDock] transcript-first drain failed',
            err,
          );
        }
        await recordingSessionApi.reprocess(session.id).catch((err) => {
        console.warn('[RecordingDock] transcript-first reprocess failed', err);
        recoveryFiredRef.current = false;
        // The overlay is locked open because completionIntent is set and
        // we have no socket scribe.error to bounce off (REST path returned
        // a synchronous 4xx). Tear it down here so the doctor isn't stuck
        // staring at "Generating SOAP note 0/6" after every refresh.
        const sid = session.id;
        const code = (err as { response?: { data?: { code?: string } } })
          ?.response?.data?.code;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 409 || status === 410 || status === 422) {
          clearCompletionIntent(sid);
          failSynthJob(sid, code ?? 'reprocess_failed');
          const description =
            code === 'AUDIO_NOT_ON_SERVER'
              ? isRTL
                ? 'الصوت غير متوفر على الخادم. حاول من الجهاز الأصلي أو تجاهل الجلسة.'
                : "Audio isn't on the server. Open this on the original device, or discard the session."
              : code === 'SYNTH_ABANDONED'
                ? isRTL
                  ? 'تم تجاوز الحد الأقصى للمحاولات. اكتب الملاحظة يدوياً من النص.'
                  : 'Synth retry cap reached. Write the note manually from the transcript.'
                : isRTL
                  ? 'تعذّر إعادة المعالجة.'
                  : "Couldn't reprocess this session.";
          toast.error(
            isRTL ? 'تعذّر إنشاء ملاحظة SOAP' : "Couldn't generate the SOAP note",
            { description, duration: 10000 },
          );
        }
      });
      })();
      return;
    }
    const sid = session.id;
    void (async () => {
      const result = await driveRecovery();
      // Socket-replayed and rest-reprocessed are happy paths — the soap-
      // result subscription handles the navigation. Skipped-finalizing
      // means another path is already in flight. Everything else is a
      // terminal error and must tear the overlay down explicitly,
      // because no scribe.error WS event will arrive (the REST 4xx
      // never went through the gateway).
      if (
        result.status === 'socket-replayed' ||
        result.status === 'rest-reprocessed' ||
        result.status === 'skipped-finalizing'
      ) {
        return;
      }
      clearCompletionIntent(sid);
      failSynthJob(sid, result.message ?? result.code ?? 'recovery_failed');
      recoveryFiredRef.current = false;
      const description =
        result.status === 'no-audio'
          ? isRTL
            ? 'الصوت غير متوفر على الخادم. حاول من الجهاز الأصلي أو تجاهل الجلسة.'
            : "Audio isn't on the server. Open this on the original device, or discard the session."
          : result.status === 'synth-abandoned'
            ? isRTL
              ? 'تم تجاوز الحد الأقصى للمحاولات. اكتب الملاحظة يدوياً من النص.'
              : 'Synth retry cap reached. Write the note manually from the transcript.'
            : result.status === 'rate-limited'
              ? isRTL
                ? `تم الوصول لحد إعادة المعالجة. حاول بعد ${result.retryAfterSeconds ?? 60} ثانية.`
                : `Reprocess rate limit hit. Try again in ${result.retryAfterSeconds ?? 60} seconds.`
              : result.status === 'rest-drain-failed'
                ? isRTL
                  ? 'تعذّر رفع الصوت. تحقّق من الاتصال وحاول مرّة أخرى.'
                  : "Couldn't upload the audio. Check your connection and try again."
                : result.message ??
                  (isRTL
                    ? 'تعذّر إعادة المعالجة.'
                    : "Couldn't reprocess this session.");
      toast.error(
        isRTL
          ? 'تعذّر إنشاء ملاحظة SOAP'
          : "Couldn't generate the SOAP note",
        { description, duration: 10000 },
      );
    })();
  }, [
    session,
    session?.id,
    session?.transcript,
    finalizing,
    draftPresent,
    completionIntentSet,
    serverSynthInFlight,
    connected,
    driveRecovery,
    failSynthJob,
    isRTL,
  ]);

  // Single terminal-state handler. The bridge mounted at the app shell
  // maps `scribe.soap-result` and `scribe.error` onto the synth job in
  // `useSynthJobsStore` (status='done' on success; 'failed' on error).
  // Subscribing to OUR session's job here means every path — same-tab
  // Complete, cross-tab event after attach, polling-based recovery —
  // funnels through the same transition. One consumer, one place where
  // the FE session lifecycle ends.
  //
  // Always clear session when the job reaches a terminal state, even
  // without a recordId (no-patient synth, soft-failure). Without this,
  // the dock would stay mounted forever — and phone-scribe / next-
  // consult flows would be blocked by the lingering session.
  //
  // Navigation only fires when there's a real recordId AND the doctor
  // didn't minimize the overlay (in which case the bridge's success
  // toast offers the explicit "Open" button — the right affordance for
  // backgrounded synths).
  useEffect(() => {
    if (!session?.id) return;
    if (!synthJob) return;
    const terminal =
      synthJob.status === 'completed' || synthJob.status === 'failed';
    if (!terminal) return;
    const recordId = synthJob.recordId;
    const sid = session.id;
    clearCompletionIntent(sid);
    useRecordingSessionStore.setState({ session: null });
    if (synthJob.status === 'completed' && recordId && !overlayMinimized) {
      /* STOPPING LANDS ON THE NOTE. Under the design shell that is
         `/encounter/:id` — the editable note the canvas ends the consult on,
         where the doctor reviews S/O/A/P and signs. The previous shell has no
         such screen, so it keeps the review page it was built around. Without
         this the spine breaks at its most important joint: the whole consult
         runs in the new design and then exits into the old one. */
      navigate(noteRoute(recordId));
    }
    // Drop the synth job from the pill so the doctor doesn't see a
    // stale chip on the next consult. The success/failure toast from
    // the bridge already conveyed the outcome.
    dismissSynthJob(synthJob.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synthJob?.status, synthJob?.recordId, session?.id, overlayMinimized]);

  // Recovery polling fallback. Only fires when synth is supposedly in
  // flight but the socket event never landed (refresh between Complete
  // and soap-result, or backend restart mid-synth). Bumps the synth job
  // through `complete()` when the rehydrated row carries medicalRecordId
  // — that funnels into the unified navigate effect above. No direct
  // navigation from here. 15s interval is generous because the bridge's
  // socket subscription is still active and will fire faster in the
  // happy path; this is the offline-recovery backstop.
  useEffect(() => {
    if (!session || !synthInFlight) return;
    if (!session.patientId) return;
    if (finalizing) return; // same-tab path — bridge's socket subscription handles it
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        await useRecordingSessionStore.getState().rehydrate();
        const latest = useRecordingSessionStore.getState().session;
        if (!latest || cancelled) return;
        let recordId = latest.medicalRecordId ?? null;
        const synthDone =
          Boolean(recordId) ||
          Boolean(
            latest.soapDraft &&
              (latest.soapDraft.subjective ||
                latest.soapDraft.objective ||
                latest.soapDraft.assessment ||
                latest.soapDraft.plan),
          );
        if (!synthDone) return;
        if (!recordId) {
          const committed = await ensureCommitted();
          recordId = committed?.id ?? null;
        }
        if (cancelled || !recordId || !latest.id) return;
        clearInterval(interval);
        // Funnel into the unified navigate effect by completing the job.
        // The bridge does this on socket events; here it's manual after
        // a successful rehydrate.
        useSynthJobsStore.getState().complete(latest.id, {
          recordId,
          patientId: latest.patientId ?? null,
        });
      } catch (err) {
        console.warn('[RecordingDock] poll rehydrate failed', err);
      }
    }, 15_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synthInFlight, finalizing, session?.id]);

  const handleAssign = () => {
    if (!session) return;
    // Drop the stage before navigating: it's `fixed inset-0`, so leaving it
    // up would hide the patient picker we're sending the doctor to.
    setConsultView('docked');
    navigate(`/patients?attachSession=${session.id}`);
  };

  // Tell the layout store our mobile mini-bar height so the launcher
  // pill can dodge it. Hooks must run before the early return below;
  // we report 0 when the dock is hidden (no session, mobile expanded
  // sheet, or finalizing-but-minimized) so the pill drops back to its
  // normal corner.
  const mobileMiniBarVisible =
    Boolean(session) && !mobileExpanded && consultView !== 'stage';
  useEffect(() => {
    setBottomHeight('recording', mobileMiniBarVisible ? 60 : 0);
    return () => {
      setBottomHeight('recording', 0);
    };
  }, [mobileMiniBarVisible, setBottomHeight]);

  // Open the sheet automatically when a NEW recording starts. Desktop
  // shows the full right rail on start; mobile used to drop the doctor
  // onto a 60px mini bar, so the live transcript — the whole point of
  // watching MAIE work, and the payoff of the demo consult — was behind
  // an unmarked tap. Keyed on session id so collapsing it stays
  // collapsed, and so a refresh/rehydrate mid-consult doesn't yank the
  // sheet back open over whatever the doctor navigated to.
  const autoExpandedForRef = useRef<string | null>(null);
  useEffect(() => {
    const sid = session?.id;
    if (!sid) return;
    if (autoExpandedForRef.current === sid) return;
    autoExpandedForRef.current = sid;
    if (session?.status !== 'capturing') return;
    // Only for a session that started in THIS tab — a rehydrated one
    // from a previous page load shouldn't hijack the screen. And never
    // over the recovery banner, which is its own decision surface.
    if (!isTabRecorder(sid) || coldMountRecovering) {
      // A session we merely picked up (other tab, post-refresh rehydrate)
      // takes the rail, not the screen the doctor is already using.
      setConsultView('docked');
      return;
    }
    setConsultView('stage');
  }, [session?.id, session?.status, coldMountRecovering, setConsultView]);

  /* THE DESIGN SKIN IS PER BROWSER, like the rest of the shell — read once
     per render rather than stored, so flipping `?maie=1` needs no reload. */
  const maieSkin = isMaieShell();

  /**
   * What the capture carries in from the file, and what else it is collecting.
   *
   * "ALREADY ON FILE — RIDING ALONG" is the canvas's own promise that the room
   * never re-asks what the record knows, so this reads the patient the session
   * is attached to. It is a cache read in the common case: the chart bundle has
   * usually already seeded `patients.detail` by the time a visit starts.
   */
  const stagePatient = useQuery({
    queryKey: patientKeys.detail(session?.patientId ?? ''),
    queryFn: () => patientApi.getPatientById(session?.patientId ?? ''),
    enabled: Boolean(maieSkin && consultView === 'stage' && session?.patientId),
    staleTime: 60_000,
  });

  const stageSafety = useMemo(() => {
    const p = stagePatient.data?.data as
      | { allergies?: string; medicalHistory?: string[]; lastVisitDate?: string }
      | undefined;
    return {
      allergies: splitAllergyText(p?.allergies).map((a) => allergenOf(a) || a),
      conditions: (p?.medicalHistory ?? []).filter(Boolean),
      /* The canvas's heading is "first presentation" vs a follow-up. A visit
         is a first presentation when the file has nothing before it. */
      firstVisit: !(p?.lastVisitDate),
    };
  }, [stagePatient.data]);

  const collecting = useMemo<CollectedRow[]>(() => {
    const rows: CollectedRow[] = [
      {
        what: 'The room — ambient audio',
        detail: session?.captureMode === 'dictate'
          ? 'your voice only, since the capture started'
          : 'both voices, since the capture started',
        source: 'this device',
        state: connected
          ? { label: 'live', kind: 'danger' }
          : { label: 'saved on this device', kind: 'warn' },
      },
    ];
    /* Only rows with something real behind them. The canvas also lists a
       nurse's door vitals and a running flow; vitals arrive on the record
       rather than the session, and flows are not modelled at all
       (docs/BACKEND-BACKLOG.md §6), so neither is drawn as if it were live. */
    if (pendingChunks > 0) {
      rows.push({
        what: 'Audio still uploading',
        detail: `${pendingChunks} chunk${pendingChunks === 1 ? '' : 's'} on their way`,
        source: 'this device',
        state: { label: 'queued', kind: 'outline' },
      });
    }
    return rows;
  }, [session?.captureMode, connected, pendingChunks]);


  if (!session) return null;
  if (passiveAttachMode && !synthInFlight) return null;
  if (session.status === 'stopped' && !synthInFlight) return null;
  // Doctor explicitly chose "Continue in background" on the synth
  // overlay — fully hide the dock so they aren't staring at a frozen
  // right rail while they work on something else. The bridge + pill
  // (mounted at app shell) carry the synth job from here. handleComplete's
  // post-await side effects (ensureCommitted, store cleanup) still run
  // because the closure outlives the unmount.
  if (synthInFlight && overlayMinimized) return null;

  const patientName = isRTL
    ? session.patientNameAr || session.patientNameEn || ''
    : session.patientNameEn || session.patientNameAr || '';
  const isPaused = session.paused;
  // Derive every render so the timer counts up smoothly while capturing
  // and freezes precisely on pause. The store's `tick()` keeps the
  // (cheaper) cached value in sync as a fallback for non-live consumers.
  const liveElapsed = getLiveElapsedSeconds(session);
  // Freeze the timer the moment finalize starts. The local session row
  // stays in `capturing`/`lastResumedAtMs` set until the dock unmounts (we
  // don't round-trip a `stop` because the gateway is the one that
  // finalizes), so without this `getLiveElapsedSeconds` would keep adding
  // `now - lastResumedAtMs` for the entire SOAP synth wait — a 2-min
  // timeout that's already produced 40+ minute timer reads when synth
  // runs long.
  if (synthInFlight && frozenElapsedRef.current === null) {
    frozenElapsedRef.current = liveElapsed;
  } else if (!synthInFlight && frozenElapsedRef.current !== null) {
    frozenElapsedRef.current = null;
  }
  const elapsed = synthInFlight && frozenElapsedRef.current !== null
    ? frozenElapsedRef.current
    : liveElapsed;

  // Approaching the hard recording cap (per-tenant, default 60 min). The
  // cap effect above auto-fires Complete at the limit; this gives the
  // doctor advance notice instead of a surprise auto-finalize. Warning
  // window: last 10% of the cap, clamped to [1, 5] minutes — 5 min heads-up
  // on the default 60-min cap, 1 min on the 5-min floor, never more than
  // 5 min even on multi-hour therapy caps.
  const capRemainingSeconds = Math.max(0, maxRecordingSeconds - elapsed);
  const capWarnWindowSeconds = Math.min(
    300,
    Math.max(60, Math.floor(maxRecordingSeconds * 0.1)),
  );
  const capWarning =
    !synthInFlight &&
    session.status !== 'stopped' &&
    !coldMountRecovering &&
    capRemainingSeconds <= capWarnWindowSeconds;

  // The stage is for a LIVE consult only: recovery is its own decision
  // surface, and once synth starts SynthOverlay (z-50) owns the screen.
  const stageVisible =
    consultView === 'stage' && !coldMountRecovering && !synthInFlight;

  const dockBody = (layout: 'rail' | 'stage') => (
    <DockBody
      isRTL={isRTL}
      isPaused={isPaused}
      finalizing={synthInFlight}
      elapsed={elapsed}
      patientName={patientName}
      unassigned={session.unassigned}
      captureMode={session.captureMode ?? 'visit'}
      bars={bars}
      segments={segments}
      connected={connected}
      ready={ready}
      liveErr={liveErr}
      pendingChunks={pendingChunks}
      noAudioWarning={noAudioWarning}
      silenceCountdown={silenceCountdown}
      capWarning={capWarning}
      capRemainingSeconds={capRemainingSeconds}
      onExtendSilence={extendSilence}
      onTogglePause={() => (isPaused ? void resume() : void pause())}
      onComplete={() => void handleComplete()}
      onAssign={handleAssign}
      onAbandon={() => void handleConfirmDiscard()}
      demoScript={demoScript}
      onDismissDemoScript={dismissDemoScript}
      sessionId={session?.id ?? null}
      layout={layout}
    />
  );

  const body = coldMountRecovering ? (
    <RecoveryBanner
      isRTL={isRTL}
      patientName={patientName}
      durationMs={Number(session.durationMs ?? 0)}
      onResume={() => void handleRecoveryResume()}
      onStopAndRetry={() => void handleRecoveryStopAndRetry()}
      onDiscard={() => void handleRecoveryDiscard()}
    />
  ) : (
    dockBody('rail')
  );

  return (
    <>
      {/* The consult stage — a full screen for the thing the doctor is
          actually doing. Held at z-40 deliberately: below Radix dialogs
          (the discard confirm) and below SynthOverlay, above everything
          else. Minimize hands the same session to the rail below. */}
      {/* THE SAME CONSULT, THE DESIGN'S CLOTHES. Both skins are handed the same
          handlers — `handleComplete` in particular, which carries the
          completion-intent-before-await rule, the offline queue and the synth
          job registration. A second implementation of that is how a doctor
          loses a consult, so there is exactly one. */}
      {stageVisible && maieSkin && (
        <div
          className="fixed inset-0 z-40 flex flex-col"
          style={{ backgroundColor: 'var(--color-bg-primary)' }}
          role="dialog"
          aria-modal="true"
          aria-label={isRTL ? 'زيارة جارية' : 'Consult in progress'}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <MaieConsultStage
            patientName={patientName}
            allergies={stageSafety.allergies}
            conditions={stageSafety.conditions}
            elapsed={elapsed}
            isPaused={isPaused}
            connected={connected}
            ready={ready}
            pendingChunks={pendingChunks}
            segments={segments}
            bars={bars}
            collecting={collecting}
            firstVisit={stageSafety.firstVisit}
            onTogglePause={() => (isPaused ? void resume() : void pause())}
            onComplete={() => void handleComplete()}
            onAbandon={() => void handleConfirmDiscard()}
            onBack={() => setConsultView('docked')}
          />
        </div>
      )}

      {stageVisible && !maieSkin && (
        <div
          className="fixed inset-0 z-40 flex flex-col"
          style={{ backgroundColor: 'var(--color-bg-secondary)' }}
          role="dialog"
          aria-modal="true"
          aria-label={isRTL ? 'زيارة جارية' : 'Consult in progress'}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <header
            className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 flex-shrink-0"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderBottom: '1px solid var(--color-border-primary)',
            }}
          >
            <div className="min-w-0">
              <p
                className="text-[10px] uppercase tracking-[0.25em] font-semibold"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {isRTL ? 'زيارة جارية' : 'Consult in progress'}
              </p>
              <p
                className="text-sm font-semibold truncate"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {patientName || (isRTL ? 'بدون مريض' : 'No patient attached')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConsultView('docked')}
              title={
                isRTL
                  ? 'التصغير — يستمر التسجيل في الشريط الجانبي'
                  : 'Minimize — recording continues in the side dock'
              }
            >
              <Minimize2 />
              {isRTL ? 'تصغير' : 'Minimize'}
            </Button>
          </header>
          <div className="flex-1 min-h-0 flex flex-col">
            {dockBody('stage')}
          </div>
        </div>
      )}

      {/* Desktop: fixed right rail, pushes main content via being a
          flex sibling. Collapse toggle (shared with NextAppointmentDock)
          shrinks it to a 40 px sliver — the recording continues in the
          background, and the doctor can expand again any time.
          Not rendered while the stage is up: it would still claim its
          460 px of layout width behind a screen nobody can see it from. */}
      {stageVisible ? null : rightRailCollapsed ? (
        <aside
          className="hidden lg:flex flex-col items-center justify-start w-10 flex-shrink-0 py-3"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderInlineStart: '1px solid var(--color-border-primary)',
          }}
          aria-label={isRTL ? 'شريط التسجيل (مطوي)' : 'Recording dock (collapsed)'}
        >
          <button
            type="button"
            onClick={() => setRightRailCollapsed(false)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            title={isRTL ? 'إظهار شريط التسجيل' : 'Show recording dock'}
            aria-label={isRTL ? 'إظهار' : 'Expand'}
          >
            <ChevronsRight
              className="w-4 h-4"
              style={{
                transform: isRTL ? 'rotate(0)' : 'rotate(180deg)',
              }}
            />
          </button>
          <button
            type="button"
            onClick={() => setConsultView('stage')}
            className="mt-1 inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            title={isRTL ? 'فتح شاشة الزيارة' : 'Open the consult screen'}
            aria-label={isRTL ? 'ملء الشاشة' : 'Full screen'}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          {/* Recording-active indicator dot so the doctor sees the
              capture is still live even with the dock collapsed. */}
          <span
            aria-hidden
            className={isPaused ? '' : 'animate-pulse'}
            style={{
              marginTop: 8,
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: isPaused
                ? 'var(--color-warning)'
                : 'var(--color-error)',
            }}
            title={
              isPaused
                ? (isRTL ? 'متوقف مؤقتاً' : 'Paused')
                : (isRTL ? 'جاري التسجيل' : 'Recording')
            }
          />
        </aside>
      ) : (
        <aside
          className="hidden lg:flex flex-col w-[460px] xl:w-[520px] flex-shrink-0 relative"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderInlineStart: '1px solid var(--color-border-primary)',
          }}
          aria-label={isRTL ? 'شريط التسجيل' : 'Recording dock'}
        >
          <div className="absolute top-3 end-3 z-10 flex items-center gap-1">
            <button
              type="button"
              onClick={() => setConsultView('stage')}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors"
              style={{ color: 'var(--color-text-tertiary)' }}
              title={isRTL ? 'فتح شاشة الزيارة' : 'Open the consult screen'}
              aria-label={isRTL ? 'ملء الشاشة' : 'Full screen'}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setRightRailCollapsed(true)}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors"
              style={{
                color: 'var(--color-text-tertiary)',
                backgroundColor: 'transparent',
              }}
              title={isRTL ? 'طي الشريط' : 'Collapse dock'}
              aria-label={isRTL ? 'طي' : 'Collapse'}
            >
              <ChevronsRight
                className="w-4 h-4"
                style={{
                  transform: isRTL ? 'rotate(180deg)' : 'none',
                }}
              />
            </button>
          </div>
          {body}
        </aside>
      )}

      {/* Mobile: collapsed mini-bar OR expanded bottom sheet. The stage
          replaces both while it's up — on a phone it IS the sheet, full
          height, so a sheet behind it is a second backdrop for nothing. */}
      <div className={stageVisible ? 'hidden' : 'lg:hidden'}>
        {!stageVisible && mobileExpanded ? (
          <>
            <div
              className="fixed inset-0 z-40"
              style={{ backgroundColor: 'color-mix(in srgb, black 40%, transparent)' }}
              onClick={() => setMobileExpanded(false)}
            />
            <div
              className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl flex flex-col"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderTop: '1px solid var(--color-border-primary)',
                // dvh, not vh: mobile Safari's `vh` counts the space
                // behind its own collapsing toolbars, so a vh-sized
                // sheet renders taller than what's actually visible and
                // its bottom row lands under the browser chrome.
                height: 'min(88dvh, 88vh)',
                maxHeight: '88dvh',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.15)',
              }}
            >
              <div className="flex-shrink-0 relative">
                <button
                  onClick={() => setMobileExpanded(false)}
                  className="w-full flex items-center justify-center py-2"
                  aria-label={isRTL ? 'طي' : 'Collapse'}
                >
                  <span
                    className="w-10 h-1 rounded-full"
                    style={{ backgroundColor: 'var(--color-border-primary)' }}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setConsultView('stage')}
                  className="absolute top-1 end-2 inline-flex items-center justify-center w-8 h-8 rounded-md"
                  style={{ color: 'var(--color-text-tertiary)' }}
                  title={isRTL ? 'فتح شاشة الزيارة' : 'Open the consult screen'}
                  aria-label={isRTL ? 'ملء الشاشة' : 'Full screen'}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
              {body}
            </div>
          </>
        ) : (
          <MobileMiniBar
            isPaused={isPaused}
            elapsed={elapsed}
            patientName={patientName || (isRTL ? 'بدون مريض' : 'Unassigned')}
            onExpand={() => setMobileExpanded(true)}
            isRTL={isRTL}
          />
        )}
      </div>

      {/* Fullscreen synth surface — shown the moment finalize starts so the
          doctor sees pipeline progress (extract → schema → verify → drugs →
          grounding → safety) instead of staring at a frozen waveform. The
          dock's existing scribe.soap-result listener handles the navigate;
          this overlay just owns the "what is happening" UX during the wait. */}
      <SynthOverlay
        open={synthOverlayVisible && !overlayMinimized}
        isRTL={isRTL}
        title={isRTL ? 'إنشاء ملاحظة SOAP' : 'Generating SOAP note'}
        subtitle={
          patientName ? (isRTL ? `للمريض ${patientName}` : `for ${patientName}`) : undefined
        }
        sessionId={session?.id ?? null}
        // Live transcript preview inside the overlay — pulled from the
        // synth-jobs store so when the post-process pass emits
        // `scribe.transcript-refined`, the bridge writes the refined
        // text into the store and the overlay's preview swaps in
        // place with a `Refined` badge.
        transcript={synthJob?.transcript ?? session?.transcript ?? null}
        transcriptIsRefined={synthJob?.transcriptIsRefined ?? false}
        onResult={() => {
          // Navigation is owned by handleComplete / the soap-result effect
          // above. The overlay just dismisses itself when synthInFlight
          // flips false (which the navigation will trigger).
        }}
        onError={(message) => {
          // Tear the overlay down. Without this it sticks open across
          // refreshes because synthInFlight stays true (completion intent
          // is in localStorage), the bridge swallows the error if no synth
          // job is registered, and the recovery driver re-fires reprocess
          // on every mount — locking the doctor on a frozen 0/6 spinner.
          if (session?.id) {
            clearCompletionIntent(session.id);
            failSynthJob(session.id, message);
          }
          setFinalizing(false);
          recoveryFiredRef.current = false;
          toast.error(
            isRTL
              ? 'تعذّر إنشاء ملاحظة SOAP'
              : "Couldn't generate the SOAP note",
            {
              description: message,
              duration: 10000,
              action: {
                label: isRTL ? 'إعادة المحاولة' : 'Retry',
                onClick: () => {
                  if (!session?.id) return;
                  dismissSynthJob(session.id);
                  // Re-arm and let the recovery driver pick it up again
                  // on the next render. driveRecovery handles socket vs
                  // REST fallback internally.
                  setCompletionIntent(session.id);
                  void driveRecovery();
                },
              },
            },
          );
        }}
        // Gives the overlay's 45s stall watchdog a working "Try again"
        // button (it only renders when onRetry is provided — previously the
        // dock never passed one, so a stalled synth offered no retry path
        // short of a full refresh). Mirrors the toast retry action.
        onRetry={() => {
          if (!session?.id) return;
          dismissSynthJob(session.id);
          setCompletionIntent(session.id);
          recoveryFiredRef.current = false;
          void driveRecovery();
        }}
        onMinimize={() => setOverlayMinimized(true)}
      />

      {/* Themed destructive confirm for both discard entry points. */}
      <ConfirmationModal
        isOpen={discardConfirm !== null}
        onClose={() => {
          if (!discarding) setDiscardConfirm(null);
        }}
        onConfirm={() => void performDiscard()}
        variant="danger"
        title={isRTL ? 'حذف التسجيل؟' : 'Discard this recording?'}
        message={
          discardConfirm === 'live'
            ? isRTL
              ? `سيُحذف التسجيل (${formatSeconds(Math.max(0, Math.floor(elapsed)))}) نهائياً — لن يُحفظ أي صوت أو نص.`
              : `${formatSeconds(Math.max(0, Math.floor(elapsed)))} of audio and the transcript so far will be permanently deleted. This cannot be undone.`
            : isRTL
              ? 'سيتم حذف هذا التسجيل نهائياً. لا يمكن التراجع.'
              : 'The recording and its transcript will be permanently deleted. This cannot be undone.'
        }
        confirmText={isRTL ? 'حذف التسجيل' : 'Discard recording'}
        cancelText={isRTL ? 'متابعة التسجيل' : 'Keep recording'}
        isLoading={discarding}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface RecoveryBannerProps {
  isRTL: boolean;
  patientName: string;
  durationMs: number;
  onResume: () => void;
  onStopAndRetry: () => void;
  onDiscard: () => void;
}

/**
 * Cold-mount recovery surface. Renders inside the dock when a session
 * is in `capturing` state but THIS tab isn't the recorder (sessionStorage
 * marker absent — see lib/recordingTabMarker). Avoids the "open the EMR
 * on Tuesday morning, watch your laptop silently start recording your
 * commute into yesterday's consult" failure mode.
 */
const RecoveryBanner: React.FC<RecoveryBannerProps> = ({
  isRTL,
  patientName,
  durationMs,
  onResume,
  onStopAndRetry,
  onDiscard,
}) => {
  const minutes = Math.floor(durationMs / 60000);
  const seconds = Math.floor((durationMs % 60000) / 1000);
  const durationLabel = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'var(--color-warning)',
            }}
          />
          <span
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-warning)' }}
          >
            {isRTL ? 'تسجيل متوقف' : 'Recording paused'}
          </span>
        </div>
        <h2
          className="text-base font-semibold leading-snug"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {isRTL
            ? 'هل تريد المتابعة على هذا الجهاز؟'
            : 'Do you want to continue on this device?'}
        </h2>
        <p
          className="text-sm mt-2"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {isRTL
            ? `جلسة ${patientName || 'بدون مريض'} (${durationLabel}) أوقفناها عند تحديث الصفحة. اختر ما تفعله قبل أن نلتقط الميكروفون.`
            : `${patientName || 'Unassigned'}'s session (${durationLabel}) was paused when the page reloaded. Pick what to do before we re-acquire the mic.`}
        </p>
      </div>
      <div className="px-5 pb-5 pt-2 flex flex-col gap-2">
        <button
          onClick={onResume}
          className="w-full inline-flex items-center justify-center gap-2 min-h-[40px] px-3 rounded-md text-sm font-semibold"
          style={{
            backgroundColor: 'var(--color-primary-600)',
            color: 'white',
          }}
        >
          {isRTL ? 'استئناف على هذا الجهاز' : 'Resume on this device'}
        </button>
        <button
          onClick={onStopAndRetry}
          className="w-full inline-flex items-center justify-center gap-2 min-h-[40px] px-3 rounded-md text-sm font-semibold"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border-primary)',
            color: 'var(--color-text-primary)',
          }}
        >
          {isRTL
            ? 'إنهاء وإنشاء ملاحظة من الصوت المحفوظ'
            : 'Finalize from saved audio'}
        </button>
        <button
          onClick={onDiscard}
          className="w-full inline-flex items-center justify-center gap-2 min-h-[40px] px-3 rounded-md text-sm font-semibold"
          style={{
            backgroundColor: 'transparent',
            color: 'var(--color-error)',
          }}
        >
          {isRTL ? 'تجاهل التسجيل' : 'Discard recording'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Read-only badge for the capture mode the doctor picked before recording
 * (Visit = doctor + patient, speaker labels; Dictation = single voice). It's
 * a read-OUT, not a control: mode + speaking language are chosen up front in
 * the arm panel, so nothing here mutates the pipeline (or a global pref)
 * mid-consult — that inline language switch used to silently rewrite the
 * doctor's default and is deliberately gone.
 */
const CaptureModePill: React.FC<{
  mode: 'visit' | 'dictate';
  isRTL: boolean;
}> = ({ mode, isRTL }) => {
  const isDictate = mode === 'dictate';
  const label = isDictate
    ? isRTL
      ? 'إملاء'
      : 'Dictation'
    : isRTL
      ? 'زيارة'
      : 'Visit';
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-primary)',
        color: 'var(--color-text-secondary)',
      }}
    >
      {isDictate ? (
        <Mic className="w-3 h-3" aria-hidden />
      ) : (
        <Users className="w-3 h-3" aria-hidden />
      )}
      {label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface DockBodyProps {
  isRTL: boolean;
  isPaused: boolean;
  finalizing: boolean;
  elapsed: number;
  patientName: string;
  unassigned: boolean;
  /** Capture mode → drives the read-only pill + whether speaker labels show. */
  captureMode: 'visit' | 'dictate';
  bars: number[];
  segments: TranscriptSegment[];
  connected: boolean;
  ready: boolean;
  liveErr: string | null;
  pendingChunks: number;
  noAudioWarning: boolean;
  silenceCountdown: number | null;
  /** True inside the approaching-hard-cap window (last ~10% / ≤5 min). */
  capWarning: boolean;
  capRemainingSeconds: number;
  onExtendSilence: () => void;
  onTogglePause: () => void;
  onComplete: () => void;
  onAssign: () => void;
  onAbandon: () => void;
  /** Demo consult read-aloud script (already language-resolved), or null. */
  demoScript: string | null;
  onDismissDemoScript: () => void;
  /** Capture session id — scopes the ledger rail to this surface. */
  sessionId: string | null;
  /**
   * `rail` (default) — the narrow dock column. `stage` — the full-screen
   * consult surface, which splits the same blocks into two panes.
   */
  layout?: 'rail' | 'stage';
}

const DockBody: React.FC<DockBodyProps> = ({
  isRTL,
  isPaused,
  finalizing,
  elapsed,
  patientName,
  unassigned,
  captureMode,
  bars,
  segments,
  connected,
  ready,
  liveErr,
  pendingChunks,
  noAudioWarning,
  silenceCountdown,
  capWarning,
  capRemainingSeconds,
  onExtendSilence,
  onTogglePause,
  onComplete,
  onAssign,
  onAbandon,
  demoScript,
  onDismissDemoScript,
  sessionId,
  layout = 'rail',
}) => {
  // Every block below renders identically in both layouts — only the
  // arrangement differs. `rail` stacks them in one ~460 px column (the
  // desktop dock / mobile sheet). `stage` is the full-screen consult:
  // capture state on the left, what the scribe is hearing on the right,
  // one full-width action bar along the bottom.

  // Header: timer + Sovereign AI chip. Discard moved to the action
  // row below — the previous header X sat directly under the rail's
  // `>>` collapse chevron and was un-clickable on the desktop dock.
  // The end-padding reserves room for the collapse button so the
  // recording status label never sits beneath it.
  const headerBlock = (
    <div
      className="flex-shrink-0 px-5 pt-3 pb-2 sm:pt-5 sm:pb-3"
      style={{ paddingInlineEnd: layout === 'stage' ? undefined : '2.5rem' }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className={isPaused ? '' : 'animate-pulse'}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: isPaused ? 'var(--color-warning)' : 'var(--color-error)',
          }}
        />
        <span
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: isPaused ? 'var(--color-warning)' : 'var(--color-error)' }}
        >
          {isPaused ? (isRTL ? 'متوقف مؤقتاً' : 'Paused') : (isRTL ? 'جاري التسجيل' : 'Recording')}
        </span>
      </div>
      <div
        className={`font-mono tabular-nums font-semibold tracking-tight ${
          layout === 'stage' ? 'text-5xl lg:text-6xl' : 'text-3xl sm:text-5xl'
        }`}
        style={{
          color: capWarning && !finalizing
            ? 'var(--color-warning)'
            : 'var(--color-text-primary)',
        }}
      >
        {formatSeconds(elapsed)}
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <SovereignAIChip isRTL={isRTL} />
        <ConnectionChip
          isRTL={isRTL}
          connected={connected}
          ready={ready}
          liveErr={liveErr}
          pendingChunks={pendingChunks}
        />
      </div>
      {/* Capture-mode badge (read-only). Mode + speaking language are set
          before recording in the arm panel; nothing is changed mid-consult.
          Hidden on phones — it's a passive label competing for the space
          the transcript needs; the mode is already fixed for the consult. */}
      <div className="mt-3 hidden sm:block">
        <CaptureModePill mode={captureMode} isRTL={isRTL} />
      </div>
    </div>
  );

  // Real waveform — driven by mic AnalyserNode. The fullscreen
  // SynthOverlay sits on top of the dock during finalize, so we
  // don't need a separate inline panel here.
  const waveformBlock = (
    <div className="flex-shrink-0 px-5 pb-2 sm:pb-3">
      <LiveWaveform bars={bars} active={!isPaused} />
    </div>
  );

  // Approaching the hard recording cap — advance notice so the
  // auto-finalize at the limit is never a surprise. Shown regardless of
  // pause state (the cap clock only counts active time, but a doctor
  // pausing at 58/60 min still needs to know Complete is imminent).
  const capWarningBlock = (
    capWarning && !finalizing ? (
      <div className="px-5 pb-3">
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
          }}
        >
          <Clock className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-warning)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {isRTL
                ? `سينتهي التسجيل خلال ${formatSeconds(capRemainingSeconds)}`
                : `Recording ends in ${formatSeconds(capRemainingSeconds)}`}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-secondary)' }}>
              {isRTL
                ? 'بلغتَ الحد الأقصى للمدة — سنُكمل الملاحظة تلقائياً عند انتهائه.'
                : 'Session length limit reached soon — the note will auto-complete at the limit.'}
            </div>
          </div>
          <button
            type="button"
            onClick={onComplete}
            className="px-3 min-h-[36px] rounded-md text-sm font-semibold flex-shrink-0"
            style={{ backgroundColor: 'var(--color-primary-600)', color: '#fff' }}
          >
            {isRTL ? 'إنهاء الآن' : 'Complete now'}
          </button>
        </div>
      </div>
    ) : null
  );

  // Audio safety rails — sustained-silence countdown takes priority over
  // the no-signal hint. Hidden while paused (timers are pinned) and during
  // finalize (audio's already captured).
  const audioRailsBlock = (
    !isPaused && !finalizing && silenceCountdown !== null ? (
      <div className="px-5 pb-3">
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
          }}
        >
          <MicOff className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-warning)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {isRTL
                ? `لا يوجد صوت — سيُنهى التسجيل خلال ${silenceCountdown} ث`
                : `No audio — ending in ${silenceCountdown}s`}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-secondary)' }}>
              {isRTL ? 'سنحفظ ما سُجِّل حتى الآن.' : "We'll save what was captured so far."}
            </div>
          </div>
          <button
            type="button"
            onClick={onExtendSilence}
            className="px-3 min-h-[36px] rounded-md text-sm font-semibold flex-shrink-0"
            style={{ backgroundColor: 'var(--color-primary-600)', color: '#fff' }}
          >
            {isRTL ? 'متابعة التسجيل' : 'Keep recording'}
          </button>
        </div>
      </div>
    ) : !isPaused && !finalizing && noAudioWarning ? (
      <div className="px-5 pb-3">
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
            border: '1px dashed color-mix(in srgb, var(--color-warning) 40%, transparent)',
          }}
        >
          <MicOff className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-warning)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {isRTL ? "لا نسمع أي صوت" : "We're not picking up any audio"}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-secondary)' }}>
              {isRTL ? 'تحقّق من الميكروفون أو الجهاز المحدد.' : 'Check your microphone or selected input device.'}
            </div>
          </div>
        </div>
      </div>
    ) : null
  );

  // What the rolling ledger has extracted so far. Self-hiding: renders
  // nothing unless EXTENDED_RECORDING_LEDGER is on AND facts have
  // arrived, so this is invisible for every tenant today.
  const ledgerBlock = (
    <LedgerPickupRail sessionId={sessionId} />
  );

  // Patient context
  const patientBlock = (
    <div className="flex-shrink-0 px-5 pb-2 sm:pb-3">
      {unassigned ? (
        <button
          onClick={onAssign}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start transition-colors"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
            border: '1px dashed color-mix(in srgb, var(--color-warning) 40%, transparent)',
            color: 'var(--color-text-primary)',
          }}
        >
          <span
            className="w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--color-warning) 18%, transparent)',
              color: 'var(--color-warning)',
            }}
          >
            <UserPlus className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold">
              {isRTL ? 'بدون مريض' : 'Unassigned'}
            </span>
            <span className="block text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {isRTL ? 'انقر لإرفاق مريض' : 'Tap to attach a patient'}
            </span>
          </span>
        </button>
      ) : (
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-xl"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
          }}
        >
          <span
            className="w-8 h-8 rounded-full inline-flex items-center justify-center text-xs font-semibold flex-shrink-0"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 14%, transparent)',
              color: 'var(--color-primary-700, #1d4ed8)',
            }}
          >
            {initials(patientName)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
              {patientName || (isRTL ? 'مريض' : 'Patient')}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>
              {isRTL ? 'جلسة نشطة' : 'active session'}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Demo consult: keep the /welcome read-aloud script on screen while
  // recording — it used to vanish the moment the demo started.
  const scriptBlock = (
    demoScript && !finalizing ? (
      <div className="flex-shrink-0 px-5 pb-2 sm:pb-3">
        <div
          className="rounded-xl px-3 py-2.5"
          style={{
            backgroundColor:
              'color-mix(in srgb, var(--color-primary-500) 6%, transparent)',
            border:
              '1px solid color-mix(in srgb, var(--color-primary-500) 18%, transparent)',
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <span
              className="text-[10px] uppercase tracking-[0.2em] font-semibold"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {isRTL ? 'اقرأ هذا بصوتك' : 'Read this out loud'}
            </span>
            <button
              type="button"
              onClick={onDismissDemoScript}
              className="ms-auto inline-flex items-center justify-center w-6 h-6 rounded-md"
              style={{ color: 'var(--color-text-tertiary)' }}
              aria-label={isRTL ? 'إخفاء النص المقترح' : 'Hide suggested script'}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p
            // whitespace-pre-line: the ambient script is a DOCTOR/PATIENT
            // dialogue, one turn per line. Collapsed into a paragraph it
            // is unreadable to perform from.
            className="text-sm leading-relaxed whitespace-pre-line max-h-32 sm:max-h-48 overflow-y-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {demoScript}
          </p>
        </div>
      </div>
    ) : null
  );

  // Live transcript — frozen but still readable during finalize so the
  // doctor can scan what was captured while SOAP synth runs.
  const transcriptBlock = (
    <LiveTranscriptPane
      segments={segments}
      isPaused={isPaused}
      ready={ready}
      isRTL={isRTL}
      finalizing={finalizing}
      dictation={captureMode === 'dictate'}
    />
  );

  // Actions — three buttons: Discard (tertiary, destructive),
  // Pause/Resume (secondary), Complete (primary). Discard sits
  // first so it's never under the rail's `>>` collapse chevron and
  // its destructive nature is acknowledged via a confirm dialog at
  // the dock level.
  const actionsBlock = (
    <div
      className="flex-shrink-0 px-5 py-3 sm:py-4 flex items-center gap-2"
      style={{
        borderTop: '1px solid var(--color-border-primary)',
        backgroundColor: 'var(--color-bg-primary)',
        // Clears the iOS home indicator / Safari bottom bar, which was
        // overlapping Discard and Complete on the phone sheet.
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
      }}
    >
      <button
        onClick={onAbandon}
        disabled={finalizing}
        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
        style={{
          backgroundColor: 'transparent',
          border: '1px solid var(--color-border-primary)',
          color: 'var(--color-error)',
        }}
        title={isRTL ? 'حذف التسجيل' : 'Discard recording'}
        aria-label={isRTL ? 'حذف التسجيل' : 'Discard recording'}
      >
        <X className="w-4 h-4" />
        <span className="hidden sm:inline">{isRTL ? 'حذف' : 'Discard'}</span>
      </button>
      <button
        onClick={onTogglePause}
        disabled={finalizing}
        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)',
          color: 'var(--color-text-primary)',
        }}
      >
        {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
        {isPaused ? (isRTL ? 'استكمال' : 'Resume') : (isRTL ? 'إيقاف' : 'Pause')}
      </button>
      <button
        onClick={onComplete}
        disabled={finalizing}
        className="flex-[1.3] inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
        style={{
          backgroundColor: 'var(--color-primary-600)',
          color: 'white',
          boxShadow: '0 1px 2px color-mix(in srgb, var(--color-primary-600) 30%, transparent)',
        }}
      >
        {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
        {finalizing
          ? (isRTL ? 'جاري التوليد…' : 'Generating SOAP…')
          : (isRTL ? 'إنهاء' : 'Complete')}
      </button>
    </div>
  );

  if (layout === 'stage') {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          {/* Left: the state of the capture — timer, signal, who it's for. */}
          <div
            // On a phone this pane sits ABOVE the transcript, so it is
            // capped and scrolls: a cap warning plus a silence warning plus
            // the demo script would otherwise push the live transcript —
            // the thing the doctor opened this screen for — off the bottom.
            className="flex flex-col flex-shrink-0 max-h-[55vh] overflow-y-auto lg:max-h-none lg:w-[420px] xl:w-[460px] lg:min-h-0"
            style={{ borderInlineEnd: '1px solid var(--color-border-primary)' }}
          >
            {headerBlock}
            {waveformBlock}
            {capWarningBlock}
            {audioRailsBlock}
            {patientBlock}
            {scriptBlock}
          </div>
          {/* Right: what the scribe has actually heard. This pane is the
              reason the stage exists — in the 460 px rail the live
              transcript was four visible lines under everything else. */}
          <div className="flex flex-col flex-1 min-h-0 pt-3 sm:pt-5">
            {ledgerBlock}
            {transcriptBlock}
          </div>
        </div>
        {actionsBlock}
      </div>
    );
  }

  // `min-h-0` is load-bearing: without it this flex child refuses to
  // shrink below its content height, so on the mobile sheet (a bounded
  // 88dvh box) the transcript pushed the action row clean off the
  // bottom edge as text arrived. With it, the transcript is the only
  // thing that gives, and Pause/Complete never move.
  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {headerBlock}
      {waveformBlock}
      {capWarningBlock}
      {audioRailsBlock}
      {ledgerBlock}
      {patientBlock}
      {scriptBlock}
      {transcriptBlock}
      {actionsBlock}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SovereignAIChip: React.FC<{ isRTL: boolean }> = ({ isRTL }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
    style={{
      backgroundColor: 'color-mix(in srgb, var(--color-secondary-500) 12%, var(--color-bg-primary))',
      color: 'var(--color-secondary-700, #4338CA)',
      border: '1px solid color-mix(in srgb, var(--color-secondary-500) 25%, transparent)',
    }}
    title={
      isRTL
        ? 'الذكاء الاصطناعي محلي — بياناتك لا تغادر خادمك'
        : 'Self-hosted AI · patient data never leaves your servers'
    }
  >
    <Shield className="w-3 h-3" />
    {isRTL ? 'ذكاء سيادي' : 'Sovereign AI'}
  </span>
);

/** Chunks that may sit un-acked during a healthy rotation (the current
 *  in-flight chunk + one riding the ack round-trip). Above this while
 *  *online*, the socket ack / REST fallback path is failing and the doctor
 *  needs to know their audio hasn't reached the server. */
const PENDING_BACKLOG_THRESHOLD = 2;

const ConnectionChip: React.FC<{
  isRTL: boolean;
  connected: boolean;
  ready: boolean;
  liveErr: string | null;
  pendingChunks: number;
}> = ({ isRTL, connected, ready, liveErr, pendingChunks }) => {
  // Track real browser connectivity so a dropped socket reads as an honest
  // "Offline · saving" (audio is durably queued and syncs on reconnect)
  // rather than an indefinite, anxiety-inducing "Connecting…".
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (liveErr) {
    return (
      <span
        role="status"
        aria-live="polite"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
          color: 'var(--color-error)',
        }}
        title={liveErr}
      >
        <MicOff className="w-3 h-3" />
        {isRTL ? 'خطأ' : 'Mic error'}
      </span>
    );
  }
  if (!connected) {
    // Offline: reassure that audio is being captured and will sync. The
    // recorder keeps writing chunks to IndexedDB and the gateway session
    // (re)registers + replays them automatically once the network returns.
    if (!online) {
      return (
        <span
          role="status"
          aria-live="polite"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
            color: 'var(--color-warning)',
          }}
          title={
            isRTL
              ? 'لا يوجد اتصال — يتم حفظ الصوت محليًا وستتم مزامنته عند عودة الإنترنت'
              : 'No internet — audio is saved on this device and will sync when you reconnect'
          }
        >
          <CloudOff className="w-3 h-3" />
          {isRTL ? 'دون اتصال · يُحفظ' : 'Offline · saving'}
        </span>
      );
    }
    return (
      <span
        role="status"
        aria-live="polite"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
        {isRTL ? 'يتصل…' : 'Connecting…'}
      </span>
    );
  }
  if (!ready) {
    return (
      <span
        role="status"
        aria-live="polite"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
          color: 'var(--color-warning)',
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
        {isRTL ? 'يجهّز LLM' : 'Warming up'}
      </span>
    );
  }
  // Connected but a backlog of captured chunks hasn't been confirmed
  // durable on the server — the ack/REST fallback path is failing while
  // we look "Live". Surface it: the audio is safe in this browser's
  // IndexedDB, but closing the tab before it drains risks the tail of
  // the consult only existing on this device.
  if (pendingChunks > PENDING_BACKLOG_THRESHOLD) {
    return (
      <span
        role="status"
        aria-live="polite"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
          color: 'var(--color-warning)',
        }}
        title={
          isRTL
            ? 'الصوت محفوظ على هذا الجهاز لكنه لم يصل للخادم بعد. لا تغلق هذه النافذة — تحقّق من الاتصال إذا استمر هذا.'
            : "Audio is saved on this device but hasn't reached the server yet. Keep this tab open — check your connection if this persists."
        }
      >
        <CloudUpload className="w-3 h-3" />
        {isRTL
          ? `${pendingChunks} مقاطع لم تُرفع`
          : `${pendingChunks} chunks not saved yet`}
      </span>
    );
  }
  return (
    <span
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-success) 14%, transparent)',
        color: 'var(--color-success)',
      }}
    >
      <Mic className="w-3 h-3" />
      {isRTL ? 'مباشر' : 'Live'}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const LiveWaveform: React.FC<{ bars: number[]; active: boolean }> = ({ bars, active }) => (
  <div
    className="flex items-end gap-[3px] h-10 sm:h-16 rounded-lg px-3 py-2"
    style={{
      backgroundColor: 'color-mix(in srgb, var(--color-primary-600) 5%, transparent)',
      border: '1px solid color-mix(in srgb, var(--color-primary-600) 10%, transparent)',
    }}
    aria-hidden
  >
    {bars.map((v, i) => {
      const h = Math.max(8, Math.round((active ? v : v * 0.2) * 100));
      return (
        <span
          key={i}
          className="flex-1 rounded-full"
          style={{
            height: `${h}%`,
            minHeight: 3,
            backgroundColor: active ? 'var(--color-primary-600)' : 'var(--color-text-tertiary)',
            opacity: active ? 0.85 : 0.35,
            transition: 'height 60ms linear',
            transformOrigin: 'bottom',
          }}
        />
      );
    })}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

// Cycle through a fixed palette so each unique speaker gets a stable hue.
const SPEAKER_COLORS = [
  'var(--color-primary-700)',
  'var(--color-secondary-700, #6d28d9)',
  'var(--color-success)',
  'var(--color-warning)',
  'var(--color-error)',
];

const speakerColor = (label: string | undefined, seenSpeakers: string[]): string => {
  if (!label) return 'var(--color-text-secondary)';
  let idx = seenSpeakers.indexOf(label);
  if (idx === -1) {
    seenSpeakers.push(label);
    idx = seenSpeakers.length - 1;
  }
  return SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
};

const SPEAKER_LABEL_OVERRIDES: Record<string, { en: string; ar: string }> = {
  doctor: { en: 'Doctor', ar: 'الطبيب' },
  physician: { en: 'Doctor', ar: 'الطبيب' },
  patient: { en: 'Patient', ar: 'المريض' },
  speaker_0: { en: 'Speaker 1', ar: 'المتحدث ١' },
  speaker_1: { en: 'Speaker 2', ar: 'المتحدث ٢' },
  speaker_2: { en: 'Speaker 3', ar: 'المتحدث ٣' },
};

const formatSpeaker = (raw: string | undefined, isRTL: boolean): string | undefined => {
  if (!raw) return undefined;
  const key = raw.toLowerCase();
  // The diarizer emits zero-padded ids ("SPEAKER_00", "SPEAKER_01", ...)
  // while our override map keys are short ("speaker_0", "speaker_1"). Try
  // both: the literal lowercase first, then the de-padded form so
  // SPEAKER_00 → speaker_0 → "Speaker 1".
  const o =
    SPEAKER_LABEL_OVERRIDES[key] ??
    SPEAKER_LABEL_OVERRIDES[key.replace(/^speaker_0*(\d+)$/i, 'speaker_$1')];
  if (o) return isRTL ? o.ar : o.en;
  // Final fallback: SPEAKER_NN with no override → render as
  // "Speaker N+1" generically rather than the raw cluster id, which is
  // what doctors actually want to see.
  const m = key.match(/^speaker_0*(\d+)$/i);
  if (m) {
    const n = Number.parseInt(m[1], 10) + 1;
    return isRTL ? `المتحدث ${n}` : `Speaker ${n}`;
  }
  if (key === 'unknown') return isRTL ? 'متحدث' : 'Speaker';
  return raw;
};

const LiveTranscriptPane: React.FC<{
  segments: TranscriptSegment[];
  isPaused: boolean;
  ready: boolean;
  isRTL: boolean;
  finalizing?: boolean;
  /** Dictation mode = single voice → suppress speaker-label chips entirely. */
  dictation?: boolean;
}> = ({ segments, isPaused, ready, isRTL, finalizing, dictation }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to the latest segment.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [segments]);

  const seenSpeakers: string[] = [];
  // Fast-preview text is scaffolding, not a confirmed segment — keep it
  // out of the count so "N segments" reflects diarized turns only.
  const confirmedCount = segments.filter((s) => !s.pending).length;

  return (
    <div className="flex-1 min-h-0 flex flex-col px-5 pb-2 sm:pb-3">
      <div className="flex items-center gap-2 mb-2">
        <span aria-hidden className="w-4 h-px" style={{ backgroundColor: '#c9ad85' }} />
        <span
          className="text-[10px] uppercase tracking-[0.25em] font-semibold"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          {finalizing
            ? (isRTL ? 'النص النهائي' : 'Captured Transcript')
            : (isRTL ? 'النص الحي' : 'Live Transcript')}
        </span>
        {!isPaused && confirmedCount > 0 && (
          <span
            className="ms-auto text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {confirmedCount} {isRTL ? 'مقطع' : 'segments'}
          </span>
        )}
      </div>
      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions"
        aria-label={
          finalizing
            ? isRTL
              ? 'النص النهائي'
              : 'Captured transcript'
            : isRTL
              ? 'النص الحي'
              : 'Live transcript'
        }
        // min-h-0 (not a 200px floor) so the transcript is what yields
        // inside the bounded mobile sheet — a floor here is what pushed
        // the action row off-screen. flex-1 still lets it claim all the
        // leftover space; sm+ keeps a comfortable floor for the rail.
        className="flex-1 min-h-0 sm:min-h-[200px] overflow-y-auto rounded-lg border p-3 space-y-2 text-sm"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-border-primary)',
          color: 'var(--color-text-primary)',
          lineHeight: 1.55,
        }}
      >
        {segments.length === 0 ? (
          <div
            className="text-xs leading-snug"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {isPaused
              ? (isRTL ? 'الجلسة متوقفة. اضغط استكمال للمتابعة.' : 'Paused — resume to keep capturing.')
              : !ready
                ? (isRTL ? 'يتم تجهيز محرك التفريغ…' : 'Warming up the transcription engine…')
                : (isRTL
                    ? 'ابدأ الكلام — سيظهر النص هنا فور سماعه.'
                    : 'Start speaking — text appears here as it’s heard.')}
          </div>
        ) : (
          segments.map((s, i) => {
            // Fast-preview text (dual-cadence pass): no speaker yet — render
            // muted + italic so it reads as "still transcribing", visibly
            // distinct from the confirmed, speaker-labelled turns it will be
            // replaced by when the diarized 30s partial lands.
            if (s.pending) {
              return (
                <div key={s.id} className="flex flex-col gap-0.5">
                  <span
                    className="italic"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    {s.text}
                    <span aria-hidden className="animate-pulse"> …</span>
                  </span>
                </div>
              );
            }
            // Dictation = one voice, so speaker chips are noise — suppress
            // them and render the transcript as a clean single column.
            const sLabel = dictation ? undefined : formatSpeaker(s.speaker, isRTL);
            const sColor = speakerColor(s.speaker, seenSpeakers);
            const prev = i > 0 ? segments[i - 1] : null;
            const sameSpeaker = prev?.speaker === s.speaker;
            return (
              <div key={s.id} className="flex flex-col gap-0.5">
                {sLabel && !sameSpeaker && (
                  <span
                    className="text-[10px] uppercase tracking-[0.2em] font-semibold"
                    style={{ color: sColor }}
                  >
                    {sLabel}
                  </span>
                )}
                <span style={{ color: 'var(--color-text-primary)' }}>{s.text}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface MiniBarProps {
  isPaused: boolean;
  elapsed: number;
  patientName: string;
  onExpand: () => void;
  isRTL: boolean;
}

const MobileMiniBar: React.FC<MiniBarProps> = ({ isPaused, elapsed, patientName, onExpand, isRTL }) => (
  <button
    onClick={onExpand}
    className="fixed inset-x-3 bottom-3 z-40 flex items-center gap-3 rounded-xl px-3 py-2.5 text-start"
    style={{
      backgroundColor: 'var(--color-bg-primary)',
      border: `1px solid ${isPaused ? 'var(--color-warning)' : 'var(--color-error)'}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    }}
    aria-label={isRTL ? 'فتح شريط التسجيل' : 'Open recording dock'}
  >
    <span
      className={isPaused ? '' : 'animate-pulse'}
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: isPaused ? 'var(--color-warning)' : 'var(--color-error)',
        flexShrink: 0,
      }}
    />
    <Mic className="w-4 h-4 flex-shrink-0" style={{ color: isPaused ? 'var(--color-warning)' : 'var(--color-error)' }} />
    <span className="font-mono tabular-nums text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
      {formatSeconds(elapsed)}
    </span>
    <span className="flex-1 min-w-0 text-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>
      · {patientName}
    </span>
    {isPaused ? (
      <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
    ) : (
      <ChevronDown className="w-4 h-4 flex-shrink-0 rotate-180" style={{ color: 'var(--color-text-tertiary)' }} />
    )}
  </button>
);

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
